import React from "react";
import { useNavigate } from "react-router-dom";
import { ArrowUpRight, CircleHelp, FileText, AlertCircle } from "lucide-react";
import { useTranslation } from "react-i18next";

function PanelShell({
  title,
  meta,
  actionLabel,
  onAction,
  className = "",
  delayClass = "",
  children,
}) {
  return (
    <section className={`lender-dashboard__panel ${className} ${delayClass}`}>
      <div className="lender-dashboard__panel-header">
        <div className="lender-dashboard__panel-header-copy">
          <h2 className="lender-dashboard__panel-title">{title}</h2>
          {meta ? (
            <span className="lender-dashboard__panel-meta">{meta}</span>
          ) : null}
        </div>

        {actionLabel ? (
          <button
            type="button"
            className="lender-dashboard__panel-action"
            onClick={onAction}
          >
            {actionLabel}
          </button>
        ) : null}
      </div>

      <div className="lender-dashboard__panel-body">{children}</div>
    </section>
  );
}

function EmptyInfoCard({ icon, title, description, delayClass = "" }) {
  return (
    <div className={`lender-dashboard__info-card ${delayClass}`}>
      <div className="lender-dashboard__info-icon-wrap">{icon}</div>
      <div className="lender-dashboard__info-copy">
        <h4 className="lender-dashboard__info-title">{title}</h4>
        <p className="lender-dashboard__info-description">{description}</p>
      </div>
    </div>
  );
}

function formatUpdatedAt(value, t) {
  if (!value) return null;

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;

  const now = new Date();
  const diffMs = now - date;
  const diffMinutes = Math.max(1, Math.floor(diffMs / 60000));
  const diffHours = Math.floor(diffMinutes / 60);
  const diffDays = Math.floor(diffHours / 24);

  if (diffMinutes < 60) {
    return t("loanCasesPanel.list.updatedMinutesAgo", { count: diffMinutes });
  }

  if (diffHours < 24) {
    return t("loanCasesPanel.list.updatedHoursAgo", { count: diffHours });
  }

  if (diffDays < 7) {
    return t("loanCasesPanel.list.updatedDaysAgo", { count: diffDays });
  }

  return date.toLocaleDateString();
}

function formatLoanCaseStatus(status, t) {
  const map = {
    OPEN: t("loanCasesPanel.statuses.OPEN"),
    RESULT_RECORDED: t("loanCasesPanel.statuses.RESULT_RECORDED"),
    CLAIMED_BY_HOMEBUYER: t("loanCasesPanel.statuses.CLAIMED_BY_HOMEBUYER"),
    CANCELLED: t("loanCasesPanel.statuses.CANCELLED"),
  };

  return map[status] || status || t("loanCasesPanel.list.defaultStatus");
}

function getPriorityTone(item) {
  switch (item?.status) {
    case "OPEN":
      return "review";

    case "CLAIMED_BY_HOMEBUYER":
      return "medium";

    case "RESULT_RECORDED":
      return item?.result?.result_status === "NOT_AGREED" ? "high" : "low";

    case "CANCELLED":
      return "neutral";

    default:
      return "neutral";
  }
}

function getPriorityLabel(item, t) {
  if (item?.priority_label) return item.priority_label;

  switch (item?.status) {
    case "OPEN":
      return t("loanCasesPanel.list.priorityFinalDetails");

    case "CLAIMED_BY_HOMEBUYER":
      return t("loanCasesPanel.list.priorityClaimed");

    case "RESULT_RECORDED":
      if (item?.result?.result_status === "AGREED") {
        return t("loanCasesPanel.list.priorityAgreed");
      }

      if (item?.result?.result_status === "NOT_AGREED") {
        return t("loanCasesPanel.list.priorityNotAgreed");
      }

      return t("loanCasesPanel.list.priorityResultRecorded");

    case "CANCELLED":
      return t("loanCasesPanel.list.priorityCancelled");

    default:
      return t("loanCasesPanel.list.defaultPriority");
  }
}

function LoanCaseRow({ item, t, navigate }) {
  const priorityClass = getPriorityTone(item);
  const priorityLabel = getPriorityLabel(item, t);
  const statusLabel = formatLoanCaseStatus(item?.status, t);
  const updatedLabel = formatUpdatedAt(item?.updated_at || item?.created_at, t);

  const loanCaseId = item?.loan_case_id || item?.id;

  const applicantName =
    item?.homebuyer_name ||
    item?.primary_applicant_name ||
    item?.claimed_by_homebuyer_name ||
    t("loanCasesPanel.list.unknownHomebuyer");

  const loanCaseCode =
    item?.loan_case_code || item?.case_code || `LC-${loanCaseId || ""}`;

  const metaParts = [
    item?.country,
    item?.region,
    statusLabel,
    updatedLabel,
  ].filter(Boolean);

  return (
    <div className="lender-dashboard__data-row lender-dashboard__data-row--loan-case">
      <div className="lender-dashboard__data-row-main">
        <div
          className={`lender-dashboard__status-chip lender-dashboard__status-chip--${priorityClass}`}
        >
          {priorityLabel}
        </div>

        <div className="lender-dashboard__data-row-copy">
          <h4 className="lender-dashboard__data-row-title">
            {loanCaseCode} · {applicantName}
          </h4>

          <p className="lender-dashboard__data-row-description">
            {metaParts.join(" • ")}
          </p>
        </div>
      </div>

      <div className="lender-dashboard__data-row-actions">
        <button
          type="button"
          className="lender-dashboard__icon-button"
          aria-label={t("common.actions.open")}
          onClick={() => navigate("/lender/loan-cases")}
        >
          <ArrowUpRight size={16} />
        </button>
      </div>
    </div>
  );
}

function LoadingState() {
  return (
    <div className="lender-dashboard__shell-stack">
      <div className="lender-dashboard__placeholder-card">
        <div className="lender-dashboard__placeholder-line lender-dashboard__placeholder-line--title" />
        <div className="lender-dashboard__placeholder-line lender-dashboard__placeholder-line--text" />
        <div className="lender-dashboard__placeholder-line lender-dashboard__placeholder-line--text-short" />
      </div>
      <div className="lender-dashboard__placeholder-card">
        <div className="lender-dashboard__placeholder-line lender-dashboard__placeholder-line--title" />
        <div className="lender-dashboard__placeholder-line lender-dashboard__placeholder-line--text" />
        <div className="lender-dashboard__placeholder-line lender-dashboard__placeholder-line--text-short" />
      </div>
      <div className="lender-dashboard__placeholder-note" />
    </div>
  );
}

function EmptyState({ t }) {
  return (
    <>
      <div className="lender-dashboard__info-list">
        <EmptyInfoCard
          icon={<FileText size={18} strokeWidth={2} />}
          title={t("loanCasesPanel.empty.title1")}
          description={t("loanCasesPanel.empty.description1")}
          delayClass="lender-dashboard__fade-2"
        />
        <EmptyInfoCard
          icon={<AlertCircle size={18} strokeWidth={2} />}
          title={t("loanCasesPanel.empty.title2")}
          description={t("loanCasesPanel.empty.description2")}
          delayClass="lender-dashboard__fade-3"
        />
      </div>

      <div className="lender-dashboard__panel-note lender-dashboard__fade-4">
        <CircleHelp size={14} />
        <span>{t("loanCasesPanel.empty.footer")}</span>
      </div>
    </>
  );
}

function ListState({ items, t, navigate }) {
  return (
    <div className="lender-dashboard__data-list">
      {items.map((item, index) => (
        <LoanCaseRow
          key={
            item?.loan_case_id ||
            item?.id ||
            item?.loan_case_code ||
            `loan-case-${index}`
          }
          item={item}
          t={t}
          navigate={navigate}
        />
      ))}
    </div>
  );
}

export default function LoanCasesPanel({
  items = [],
  totalCount = 0,
  hasMore = false,
  isLoading = false,
}) {
  const { t } = useTranslation("lender-dashboard");
  const navigate = useNavigate();
  const hasItems = items.length > 0;

  const meta = hasItems
    ? t("loanCasesPanel.metaPreview", {
        shown: items.length,
        total: totalCount || items.length,
      })
    : t("loanCasesPanel.meta");

  return (
    <PanelShell
      title={t("loanCasesPanel.title")}
      meta={meta}
      actionLabel={hasItems || hasMore ? t("common.actions.viewAll") : ""}
      onAction={() => navigate("/lender/loan-cases")}
      className="lender-dashboard__panel--loan-cases"
      delayClass="lender-dashboard__fade-2"
    >
      {isLoading ? (
        <LoadingState />
      ) : hasItems ? (
        <ListState items={items} t={t} navigate={navigate} />
      ) : (
        <EmptyState t={t} />
      )}
    </PanelShell>
  );
}
